import React, { useMemo, useState } from "react";

import {
  Activity,
  Building2,
  Megaphone,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

import SuccessNotification from "../../Components/SuccessNotification";
import DeleteModal from "../../Container/DeleteModal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  deleteTenant,
  dismissDeleteTenantNotification,
  updateOrganizationCoverageAnalysisConfig,
  updateOrganizationProductTelemetricsConfig,
  updateOrganizationRlSettingConfig,
  updateOrganizationWeeklyDigestConfig,
} from "../../Store/Reducers/AdminSlice";
import { toast } from "../../Utils/Helper/toast";
import { Organization as BaseOrganization } from "../AdminOverviewContainer";
import AdminTableContainer from "../AdminTableContainer";
import AddOrganizationModal from "./AddOrganizationModal";
import OrgFeaturesDrawer, {
  ORG_FEATURE_DEFINITIONS,
  OrgFeatureKey,
} from "./OrgFeaturesDrawer";
import TelemetricDataModal from "./TelemetricDataModal";
import ToggleCoverageAnalysisModal from "./ToggleCoverageAnalysisModal";
import ToggleProductTelemetricsModal from "./ToggleProductTelemetricsModal";
import ToggleRlAssessmentsModal from "./ToggleRlAssessmentsModal";
import ToggleWeeklyDigestModal from "./ToggleWeeklyDigestModal";
import "./style.scss";

interface RlAssessmentsModalState {
  org: Organization;
  toEnabled: boolean;
  loading: boolean;
}

interface WeeklyDigestModalState {
  org: Organization;
  toEnabled: boolean;
  loading: boolean;
}

interface ProductTelemetricsModalState {
  org: Organization;
  toEnabled: boolean;
  loading: boolean;
}

interface CoverageAnalysisModalState {
  org: Organization;
  toEnabled: boolean;
  loading: boolean;
}

interface AdminOrganizationContainerProps {
  organizations: Organization[];
  onRefresh?: () => void;
  loading?: boolean;
}

interface Organization extends BaseOrganization {
  slug?: string;
}

const AdminOrganizationContainer: React.FC<AdminOrganizationContainerProps> = ({
  organizations,
  onRefresh,
  loading = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    deleteTenantLoading,
    deleteTenantNotification,
    updateOrgRlSettingTenantId,
    updateOrgWeeklyDigestTenantId,
    updateOrgProductTelemetricsTenantId,
    updateOrgCoverageAnalysisTenantId,
  } = useAppSelector((state: any) => state.admin);
  const [searchValue, setSearchValue] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [telemetryModalOpen, setTelemetryModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [featuresOrgId, setFeaturesOrgId] = useState<string | null>(null);
  const [, setSearchParams] = useSearchParams();
  const [rlAssessmentsModal, setRlAssessmentsModal] =
    useState<RlAssessmentsModalState | null>(null);
  const [weeklyDigestModal, setWeeklyDigestModal] =
    useState<WeeklyDigestModalState | null>(null);
  const [productTelemetricsModal, setProductTelemetricsModal] =
    useState<ProductTelemetricsModalState | null>(null);
  const [coverageAnalysisModal, setCoverageAnalysisModal] =
    useState<CoverageAnalysisModalState | null>(null);

  const featuresOrg = useMemo(
    () =>
      featuresOrgId
        ? (organizations.find(org => org.tenant_id === featuresOrgId) ?? null)
        : null,
    [featuresOrgId, organizations]
  );

  const featuresDrawerOpen = !!featuresOrg;

  const handleDeleteClick = (org: Organization) => {
    setSelectedOrg(org);
    setDeleteModalOpen(true);
  };

  const handleTelemetryClick = (org: Organization) => {
    setSelectedOrg(org);
    setTelemetryModalOpen(true);
  };

  const openFeaturesDrawer = (org: Organization) => {
    setFeaturesOrgId(org.tenant_id);
  };

  const closeFeaturesDrawer = () => setFeaturesOrgId(null);

  const handleConfirmDelete = async () => {
    if (selectedOrg) {
      await dispatch(deleteTenant(selectedOrg.tenant_id) as any);
      setDeleteModalOpen(false);
      setSelectedOrg(null);
      onRefresh?.();
    }
  };

  const openRlAssessmentsModal = (org: Organization, toEnabled: boolean) => {
    setRlAssessmentsModal({ org, toEnabled, loading: false });
  };

  const closeRlAssessmentsModal = () => setRlAssessmentsModal(null);

  const handleRlAssessmentsConfirm = async () => {
    if (!rlAssessmentsModal) return;

    setRlAssessmentsModal(prev => (prev ? { ...prev, loading: true } : null));

    try {
      await dispatch(
        updateOrganizationRlSettingConfig({
          tenantId: rlAssessmentsModal.org.tenant_id,
          relationshipAssessments: rlAssessmentsModal.toEnabled,
        })
      ).unwrap();
      toast.success(
        `Relationship assessments ${rlAssessmentsModal.toEnabled ? "enabled" : "disabled"} for ${rlAssessmentsModal.org.organization}`
      );
      setRlAssessmentsModal(null);
    } catch (error) {
      toast.error(
        typeof error === "string" ? error : "Failed to update RL settings"
      );
      setRlAssessmentsModal(prev =>
        prev ? { ...prev, loading: false } : null
      );
    }
  };

  const openWeeklyDigestModal = (org: Organization, toEnabled: boolean) => {
    setWeeklyDigestModal({ org, toEnabled, loading: false });
  };

  const closeWeeklyDigestModal = () => setWeeklyDigestModal(null);

  const handleWeeklyDigestConfirm = async () => {
    if (!weeklyDigestModal) return;

    setWeeklyDigestModal(prev => (prev ? { ...prev, loading: true } : null));

    try {
      await dispatch(
        updateOrganizationWeeklyDigestConfig({
          tenantId: weeklyDigestModal.org.tenant_id,
          enabled: weeklyDigestModal.toEnabled,
        })
      ).unwrap();
      toast.success(
        `Weekly Digest ${weeklyDigestModal.toEnabled ? "enabled" : "disabled"} for ${weeklyDigestModal.org.organization}`
      );
      setWeeklyDigestModal(null);
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to update Weekly Digest settings"
      );
      setWeeklyDigestModal(prev => (prev ? { ...prev, loading: false } : null));
    }
  };

  const openProductTelemetricsModal = (
    org: Organization,
    toEnabled: boolean
  ) => {
    setProductTelemetricsModal({ org, toEnabled, loading: false });
  };

  const closeProductTelemetricsModal = () => setProductTelemetricsModal(null);

  const handleProductTelemetricsConfirm = async () => {
    if (!productTelemetricsModal) return;

    setProductTelemetricsModal(prev =>
      prev ? { ...prev, loading: true } : null
    );

    try {
      await dispatch(
        updateOrganizationProductTelemetricsConfig({
          tenantId: productTelemetricsModal.org.tenant_id,
          enabled: productTelemetricsModal.toEnabled,
        })
      ).unwrap();
      toast.success(
        `Product Telemetrics ${productTelemetricsModal.toEnabled ? "enabled" : "disabled"} for ${productTelemetricsModal.org.organization}`
      );
      setProductTelemetricsModal(null);
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to update Product Telemetrics settings"
      );
      setProductTelemetricsModal(prev =>
        prev ? { ...prev, loading: false } : null
      );
    }
  };

  const openCoverageAnalysisModal = (org: Organization, toEnabled: boolean) => {
    setCoverageAnalysisModal({ org, toEnabled, loading: false });
  };

  const closeCoverageAnalysisModal = () => setCoverageAnalysisModal(null);

  const handleCoverageAnalysisConfirm = async () => {
    if (!coverageAnalysisModal) return;

    setCoverageAnalysisModal(prev =>
      prev ? { ...prev, loading: true } : null
    );

    try {
      await dispatch(
        updateOrganizationCoverageAnalysisConfig({
          tenantId: coverageAnalysisModal.org.tenant_id,
          enabled: coverageAnalysisModal.toEnabled,
        })
      ).unwrap();
      toast.success(
        `Coverage Analysis ${coverageAnalysisModal.toEnabled ? "enabled" : "disabled"} for ${coverageAnalysisModal.org.organization}`
      );
      setCoverageAnalysisModal(null);
    } catch (error) {
      toast.error(
        typeof error === "string"
          ? error
          : "Failed to update Coverage Analysis settings"
      );
      setCoverageAnalysisModal(prev =>
        prev ? { ...prev, loading: false } : null
      );
    }
  };

  const handleToggleFeature = (
    org: Organization,
    featureKey: OrgFeatureKey,
    toEnabled: boolean
  ) => {
    switch (featureKey) {
      case "rl_assessments":
        openRlAssessmentsModal(org, toEnabled);
        break;
      case "weekly_digest":
        openWeeklyDigestModal(org, toEnabled);
        break;
      case "product_telemetrics":
        openProductTelemetricsModal(org, toEnabled);
        break;
      case "coverage_analysis":
        openCoverageAnalysisModal(org, toEnabled);
        break;
      default:
        break;
    }
  };

  const filteredOrganizations = organizations.filter(
    org =>
      org.organization.toLowerCase().includes(searchValue.toLowerCase()) ||
      org.domain.toLowerCase().includes(searchValue.toLowerCase())
  );

  const columns = [
    {
      key: "name",
      title: "ORGANIZATION",
      width: "24%",
      render: (_: any, record: Organization) => (
        <div className="flex items-center gap-3 min-w-0">
          <div className="org-icon shrink-0">
            <Building2 size={18} className="h-5 w-5 text-gray-400" />
          </div>
          <div className="org-info min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {record.organization}
            </div>
            <div className="text-sm text-gray-500 mt-0.5 truncate">
              {record.slug || "-"}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "domain",
      title: "DOMAIN",
      width: "16%",
      render: (value: string) => (
        <div className="flex flex-wrap-cell">
          <div className="cell-info">
            <div className="cell-slug truncate">{value}</div>
          </div>
        </div>
      ),
    },
    {
      key: "users_count",
      title: "USERS",
      width: "8%",
      render: (value: number) => (
        <div className="flex flex-wrap-cell">
          <div className="cell-info">
            <div className="cell-slug">{value}</div>
          </div>
        </div>
      ),
    },
    {
      key: "status",
      title: "STATUS",
      width: "10%",
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 capitalize">
          {value}
        </span>
      ),
    },
    {
      key: "features",
      title: "FEATURES",
      width: "28%",
      className: "org-table-col--features",
      render: (_: any, record: Organization) => (
        <button
          type="button"
          className="org-feature-chips"
          onClick={() => openFeaturesDrawer(record)}
          title="Manage organization features"
          aria-label={`Manage features for ${record.organization}`}
        >
          {ORG_FEATURE_DEFINITIONS.map(feature => {
            const enabled = feature.isEnabled(record);
            return (
              <span
                key={feature.key}
                className={`org-feature-chip ${
                  enabled ? "org-feature-chip--on" : "org-feature-chip--off"
                }`}
              >
                {feature.shortLabel}
              </span>
            );
          })}
        </button>
      ),
    },
    {
      key: "actions",
      title: "ACTIONS",
      width: "14%",
      textalign: "center",
      className: "org-table-cell--center org-table-col--actions",
      render: (_: any, record: Organization) => (
        <div className="org-table-actions">
          <button
            type="button"
            className="org-table-action-btn"
            onClick={() => openFeaturesDrawer(record)}
            title="Organization features"
            aria-label="Organization features"
          >
            <Settings2 size={18} />
          </button>
          <button
            type="button"
            className="org-table-action-btn"
            onClick={() => handleTelemetryClick(record)}
            title="Telemetric Data"
            aria-label="Telemetric Data"
          >
            <Activity size={18} />
          </button>
          <button
            type="button"
            className="org-table-action-btn"
            onClick={() =>
              setSearchParams({
                tab: "release-notes",
                org: record.tenant_id,
              })
            }
            title="Release notes"
            aria-label="Release notes"
          >
            <Megaphone size={18} />
          </button>
          <button
            type="button"
            className="org-table-action-btn org-table-action-btn--delete"
            onClick={() => handleDeleteClick(record)}
            title="Delete"
            aria-label="Delete"
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="admin-organization-container">
        <AdminTableContainer
          columns={columns}
          data={filteredOrganizations}
          loading={loading}
          showSearch
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder="Search organizations..."
          showRefresh
          onRefresh={onRefresh}
          showAddButton
          addButtonLabel="New Organization"
          addButtonIcon={<Plus size={16} />}
          onAddClick={() => setAddModalOpen(true)}
          title="Organizations"
          maxHeight="calc(100vh - 25.5rem)"
        />
      </div>
      <OrgFeaturesDrawer
        isOpen={featuresDrawerOpen}
        org={featuresOrg}
        onClose={closeFeaturesDrawer}
        updatingIds={{
          rlAssessments: updateOrgRlSettingTenantId,
          weeklyDigest: updateOrgWeeklyDigestTenantId,
          productTelemetrics: updateOrgProductTelemetricsTenantId,
          coverageAnalysis: updateOrgCoverageAnalysisTenantId,
        }}
        onToggleFeature={handleToggleFeature}
      />
      <DeleteModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        title="Delete Organization"
        entityName={selectedOrg?.organization || ""}
        warningItems={[
          "The organization record",
          "All users belonging to this organization",
          "All executive profiles",
          "All associated data and relationships",
        ]}
        onConfirm={handleConfirmDelete}
        loading={deleteTenantLoading}
      />
      <ToggleRlAssessmentsModal
        isOpen={!!rlAssessmentsModal}
        toggle={closeRlAssessmentsModal}
        organizationName={rlAssessmentsModal?.org.organization || ""}
        isEnabling={rlAssessmentsModal?.toEnabled ?? false}
        onConfirm={handleRlAssessmentsConfirm}
        loading={rlAssessmentsModal?.loading ?? false}
        nested={featuresDrawerOpen}
      />
      <ToggleWeeklyDigestModal
        isOpen={!!weeklyDigestModal}
        toggle={closeWeeklyDigestModal}
        organizationName={weeklyDigestModal?.org.organization || ""}
        isEnabling={weeklyDigestModal?.toEnabled ?? false}
        onConfirm={handleWeeklyDigestConfirm}
        loading={weeklyDigestModal?.loading ?? false}
        nested={featuresDrawerOpen}
      />
      <ToggleProductTelemetricsModal
        isOpen={!!productTelemetricsModal}
        toggle={closeProductTelemetricsModal}
        organizationName={productTelemetricsModal?.org.organization || ""}
        isEnabling={productTelemetricsModal?.toEnabled ?? false}
        onConfirm={handleProductTelemetricsConfirm}
        loading={productTelemetricsModal?.loading ?? false}
        nested={featuresDrawerOpen}
      />
      <ToggleCoverageAnalysisModal
        isOpen={!!coverageAnalysisModal}
        toggle={closeCoverageAnalysisModal}
        organizationName={coverageAnalysisModal?.org.organization || ""}
        isEnabling={coverageAnalysisModal?.toEnabled ?? false}
        onConfirm={handleCoverageAnalysisConfirm}
        loading={coverageAnalysisModal?.loading ?? false}
        nested={featuresDrawerOpen}
      />
      <AddOrganizationModal
        isOpen={addModalOpen}
        toggle={() => setAddModalOpen(false)}
        onSuccess={() => {
          onRefresh?.();
        }}
      />
      <TelemetricDataModal
        isOpen={telemetryModalOpen}
        toggle={() => {
          setTelemetryModalOpen(false);
          setSelectedOrg(null);
        }}
        organizationName={selectedOrg?.organization || ""}
        orgId={selectedOrg?.tenant_id || ""}
      />
      <SuccessNotification
        isOpen={!!deleteTenantNotification}
        onClose={() => dispatch(dismissDeleteTenantNotification())}
        message={deleteTenantNotification?.message ?? ""}
        details={deleteTenantNotification?.details ?? ""}
        type={deleteTenantNotification?.type ?? "success"}
      />
    </>
  );
};

export default AdminOrganizationContainer;
